import React, { memo, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { View, Text, useWindowDimensions, Platform, ScrollView } from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import { Entypo } from '@expo/vector-icons';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '../../../stylesheet';
import TabNavigator from '../TabNavigation/TabNavigator';
import { ADD_TAB, CLOSE_SECOND_MENU, SET_MESSAGE_APP_FILTER, SHOW_LOCK_MODAL, SHOW_MESSAGE_MODAL, TOGGLE_MESSAGE_MODAL } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';
import { LinearGradient } from 'expo-linear-gradient';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { uniqueId } from 'lodash';
import { useNavigation } from '@react-navigation/native';
import { SmartSaaSIcon } from "@assets/--native";
import { FontAwesome6, MaterialCommunityIcons } from "@expo/vector-icons";
import MenuItem from '@mui/material/MenuItem';
import toTitleCase from '@hooks/toTitleCase';
import { selectUser, userAuths } from '@reduxLocal/selectors/userSelectors';
import { Popover, Badge, BadgeProps, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import webTransition from '@utils/webTransition';
import { JSX } from 'react/jsx-runtime';

export type ChatTabProps = {
  bgColor: string;
  icon: string | JSX.Element;
  title: string;
  callback: () => void;
  active?: boolean;
  badgeCount?: number;
};


const AddTab = ({ chatFeatures }: { chatFeatures: ChatTabProps[] }) => {
  const navigation = useNavigation<any>();
  const currentTab = useSelector((state: DefaultRootState) => state.navigation.tabs.length);
  const theme = themeSelector();
  const dispatch = useDispatch();

  // Helper function for robust navigation
  const navigateWithFallback = useCallback((routes: Array<{ stack?: string; screen?: string; params?: any }>) => {
    for (const route of routes) {
      try {
        if (route.stack && route.screen) {
          navigation.navigate(route.stack, { screen: route.screen, params: route.params });
          return true;
        } else if (route.screen) {
          navigation.navigate(route.screen, route.params);
          return true;
        }
      } catch (error) {
        console.log(`Navigation attempt failed for ${route.stack || route.screen}:`, error);
        continue;
      }
    }
    return false;
  }, [navigation]);

  const handleAddTab = useCallback(() => {
    const idGen = uniqueId();
    console.log(`Adding new tab with ID: ${idGen}`);
    
    try {
      // Dispatch the action to add a new tab
      dispatch({ 
        type: ADD_TAB, 
        payload: { 
          active: true, 
          id: idGen, 
          page: 'Welcome' 
        } 
      });
      
        // Try multiple navigation fallbacks for new tab
        const newTabRoutes = [
          { screen: 'Welcome' },
          { screen: 'Data' },
          { screen: 'Projects' },
          { screen: 'Help' },
          { stack: 'Data', screen: 'DataMenu' },
          { stack: 'Projects', screen: 'ProjectMenu' }
        ];      if (navigateWithFallback(newTabRoutes)) {
        console.log('Successfully added new tab and navigated');
      } else {
        console.error('All navigation attempts failed for new tab');
      }
    } catch (error) {
      console.error('Error adding new tab:', error);
    }
  }, [dispatch, navigateWithFallback]);

  if (currentTab < 8) {
    return (
      <Pressable 
        style={[
          styles.modernActionButton,
          getModernActionStyle(theme, 'blue'),
          {
            maxWidth: 40,
            minWidth: 40,
            overflow: 'hidden',
            paddingHorizontal: 12,
            paddingVertical: 8,
            borderWidth: 1,
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
            }),
          }
        ]} 
        onPress={handleAddTab}
      >
        <Entypo name={"plus"} size={18} color={"white"}></Entypo>
      </Pressable>
    );
  } else {
    return (
      <Pressable 
        style={[
          styles.modernActionButton,
          getModernActionStyle(theme, 'grey'),
          {
            maxWidth: 40,
            overflow: 'hidden',
            paddingHorizontal: 12,
            paddingVertical: 8,
            opacity: 0.6,
            borderWidth: 1,
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
          }
        ]}
        disabled
      >
        <Entypo name={"plus"} size={18} color={theme ? "#94a3b8" : "#cbd5e1"}></Entypo>
      </Pressable>
    );
  }
};


const FooterBar: any = () => {
  const messageAppFilter = useSelector((state: DefaultRootState) => state.UI.messageAppFilter);
  const menuOpen = useSelector((state: DefaultRootState) => state.UI.menuOpen);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width } = useWindowDimensions();
  const navigation = useNavigation<any>();
  const [messageState, setMessageState] = useState<HTMLElement | null>(null);
  const [availableFeatures, setAvailableFeatures] = useState<ChatTabProps[]>([]);
  const userAuthentications = userAuths();
  const queryClient = useQueryClient();

  // Define chat features with theme-aware icon colors
  // theme = true means light theme (use black), theme = false means dark theme (use white)
  const CHAT_FEATURES: ChatTabProps[] = React.useMemo(() => [
    {
      title: 'default',
      bgColor: "rgb(90,192,234)",
      icon: <SmartSaaSIcon fill={theme ? "black" : "white"} height={24} width={24} />,
      callback: () => { },
      badgeCount: 0
    },
    {
      title: 'messenger',
      bgColor: "#00B2FF",
      icon: <FontAwesome6 name="facebook-messenger" size={24} color={theme ? "black" : "white"} />,
      callback: () => { },
      badgeCount: 10
    },
    {
      title: 'whatsapp',
      bgColor: "#25D366",
      icon: <FontAwesome6 name="whatsapp" size={24} color={theme ? "black" : "white"} />,
      callback: () => { },
      badgeCount: 5
    },
    {
      title: 'all',
      bgColor: "#25D366",
      icon: <MaterialCommunityIcons name="message-multiple" size={24} color={theme ? "black" : "white"} />,
      callback: () => { },
      badgeCount: 0
    },
    {
      title: 'viber',
      bgColor: "#8f5db7",
      icon: <FontAwesome6 name="viber" size={24} color={theme ? "black" : "white"} />,
      callback: () => { },
      badgeCount: 2
    },
    {
      title: 'linkedIn',
      bgColor: "#0077B5",
      icon: <FontAwesome6 name="linkedin" size={24} color={theme ? "black" : "white"} />,
      callback: () => { },
      badgeCount: 8
    },
  ], [theme]);

  // Helper function to handle nested navigation with fallbacks
  const navigateWithFallback = useCallback((routes: Array<{ stack?: string; screen?: string; params?: any }>) => {
    for (const route of routes) {
      try {
        if (route.stack && route.screen) {
          // Nested navigation
          navigation.navigate(route.stack, { screen: route.screen, params: route.params });
          return true;
        } else if (route.screen) {
          // Direct navigation
          navigation.navigate(route.screen, route.params);
          return true;
        }
      } catch (error) {
        continue;
      }
    }
    return false;
  }, [navigation]);

  // Handle navigation for each tab type
  const handleTabNavigation = useCallback((feature: ChatTabProps) => {
    console.log(`Navigating to ${feature.title} tab`);
    
    switch (feature.title) {
      case 'all':
        // 'all' filter shows all WhatsApp conversations from all numbers
        // Navigation is handled by filter selection, no specific screen needed
        break;
      case 'messenger':
        // Try multiple navigation routes for messenger
        const messengerRoutes = [
          { stack: 'Mail', screen: 'Main', params: { platform: 'messenger' } },
          { stack: 'Data', screen: 'EmailTemplates', params: { platform: 'messenger' } },
          { stack: 'Messages', screen: 'External', params: { platform: 'messenger' } },
          { screen: 'Messages', params: { platform: 'messenger' } },
          { screen: 'Mail' }
        ];
        
        if (!navigateWithFallback(messengerRoutes)) {
          console.log('All messenger navigation attempts failed - opening modal');
          dispatch({ type: SHOW_MESSAGE_MODAL });
        }
        break;
        
      case 'whatsapp':
        // Try multiple navigation routes for WhatsApp
        const whatsappRoutes = [
          { stack: 'Data', screen: 'WhatsappTemplates', params: { platform: 'whatsapp' } },
          { stack: 'Data', screen: 'DataTemplates', params: { platform: 'whatsapp' } },
          { stack: 'Messages', screen: 'External', params: { platform: 'whatsapp' } },
          { screen: 'Messages', params: { platform: 'whatsapp' } },
          { screen: 'Data' }
        ];
        
        if (!navigateWithFallback(whatsappRoutes)) {
          console.log('All WhatsApp navigation attempts failed - opening modal');
          dispatch({ type: SHOW_MESSAGE_MODAL });
        }
        break;
        
      case 'viber':
        // Try multiple navigation routes for Viber
        const viberRoutes = [
          { stack: 'Data', screen: 'EmailTemplates', params: { platform: 'viber' } },
          { stack: 'Messages', screen: 'External', params: { platform: 'viber' } },
          { screen: 'Messages', params: { platform: 'viber' } },
          { screen: 'Data' }
        ];
        
        if (!navigateWithFallback(viberRoutes)) {
          console.log('All Viber navigation attempts failed - opening modal');
          dispatch({ type: SHOW_MESSAGE_MODAL });
        }
        break;
        
      case 'linkedIn':
        // Try multiple navigation routes for LinkedIn
        const linkedinRoutes = [
          { stack: 'Networking', screen: 'Search' },
          { stack: 'Networking', screen: 'Map' },
          { screen: 'Networking' }
        ];
        
        if (!navigateWithFallback(linkedinRoutes)) {
          console.log('All LinkedIn navigation attempts failed - opening modal');
          dispatch({ type: SHOW_MESSAGE_MODAL });
        }
        break;
        
      case 'default':
      default:
        // Handle default SmartSaaS navigation with fallbacks
        const defaultRoutes = [
          { screen: 'Welcome' },
          { screen: 'Data' },
          { screen: 'Help' }
        ];
        
        if (!navigateWithFallback(defaultRoutes)) {
          console.log('All default navigation attempts failed');
        }
        break;
    }
  }, [dispatch, navigateWithFallback]);

  // Update chat features with proper navigation callbacks
  const chatFeatures: ChatTabProps[] = React.useMemo(() => 
    CHAT_FEATURES.map(feature => ({
      ...feature,
      callback: () => handleTabNavigation(feature)
    })), [CHAT_FEATURES, handleTabNavigation]);

  // Filter available features based on user authentication
  React.useEffect(() => {
    const availableFeatures = chatFeatures.filter((feature) => {
      // Always show default SmartSaaS
      if (feature.title === 'default') return true;
      // Check if user has authentication for this feature
      return Object.keys(userAuthentications).includes(feature.title);
    });
    
    setAvailableFeatures(availableFeatures);
  }, [chatFeatures, userAuthentications]);

  // Get current active feature
  const getCurrentFeature = useCallback(() => {
    return availableFeatures.find(feature => feature.title === messageAppFilter) || availableFeatures[0];
  }, [availableFeatures, messageAppFilter]);

  // Get total badge count for the selected feature
  const getTotalBadgeCount = useCallback(() => {
    const selectedFeature = availableFeatures.find(feature => feature.title === messageAppFilter);
    return selectedFeature?.badgeCount || 0;
  }, [availableFeatures, messageAppFilter]);

  // Handle feature selection
  const handleFeatureSelect = useCallback((feature: ChatTabProps) => {
    
    // Set the message app filter first
    dispatch({ type: SET_MESSAGE_APP_FILTER, payload: feature.title });
    
    // Close the popover
    setMessageState(null);
    
    // Invalidate and refetch all conversations queries when filter changes
    queryClient.invalidateQueries({ 
      queryKey: ['all-users'],
      refetchType: 'active' // Only refetch active queries
    });
    
    console.log('🔄 FooterBar: Filter changed to', feature.title, '- invalidating conversations');
    
    // Execute the feature's callback for navigation
    // Add a small delay to ensure state updates are processed
    setTimeout(() => {
      feature.callback();
    }, 100);
  }, [dispatch, queryClient]);

  // Handle direct tab click (without opening popover)
  const handleDirectTabClick = useCallback(() => {
    const currentFeature = getCurrentFeature();
    if (currentFeature) {
      console.log(`Direct tab click for: ${currentFeature.title}`);
      handleTabNavigation(currentFeature);
    }
  }, [getCurrentFeature, handleTabNavigation]);
  // Initialize default filter if none is set
  React.useEffect(() => {
    if (!messageAppFilter && availableFeatures.length > 0) {
      const defaultFeature = availableFeatures.find(f => f.title === 'default') || availableFeatures[0];
      dispatch({ type: SET_MESSAGE_APP_FILTER, payload: defaultFeature.title });
      console.log(`Initialized default filter to: ${defaultFeature.title}`);
    }
  }, [messageAppFilter, availableFeatures, dispatch]);

  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: -8,
      top: -2,
      border: `0px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
    },
  }));

  const currentFeature = getCurrentFeature();

  return width > 800 && (
    <View style={[
      styles.centerEverything,
      {
        bottom: 0,
        left: menuOpen ? 270 : 70,
        right: 35,
        flexDirection: 'row',
        backgroundColor: 'transparent',
        paddingHorizontal: 0,
        paddingLeft: 0,
        paddingRight: 0,
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: 0,
        padding: 0,
        zIndex: 50,
        ...webTransition('left'),
        position: 'fixed',
      }
    ]}>
      <LinearGradient
        colors={['#C8A2C8', '#87CEEB']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[
          styles.centerEverything,
          {
            flexDirection: 'row',
            alignSelf: 'stretch',
            flex: 1,
            left: -10,
            minHeight: 30,
            borderWidth: .5,
            borderBottomWidth: 0,
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
            marginVertical: 0,
            padding: 0,
            overflow: 'hidden',
            position: 'relative',
          }
        ]}
      >
        {/* Floating blur elements for depth */}
        {Platform.OS === 'web' && (
          <>
            <View style={{
              position: 'absolute',
              top: '20%',
              left: '10%',
              width: 100,
              height: 100,
              backgroundColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(147, 197, 253, 0.15)',
              borderRadius: 50,
              ...(Platform.OS === 'web' && {
                filter: 'blur(40px)',
              }),
              zIndex: 0,
            }} />
            <View style={{
              position: 'absolute',
              bottom: '30%',
              right: '15%',
              width: 120,
              height: 120,
              backgroundColor: theme ? 'rgba(200, 162, 200, 0.25)' : 'rgba(135, 206, 235, 0.2)',
              borderRadius: 60,
              ...(Platform.OS === 'web' && {
                filter: 'blur(50px)',
              }),
              zIndex: 0,
            }} />
          </>
        )}
        <View style={{
          flex: 1,
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(20px)',
          }),
          zIndex: 1,
          flexDirection: 'row',
          alignItems: 'center',
        }}>
          {/* Add Tab Button - Left side */}
          <View style={{ paddingLeft: 8, paddingVertical: 8 }}>
            <AddTab chatFeatures={chatFeatures} />
          </View>

          {/* Scrollable Tabs Container */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: 8,
              paddingHorizontal: 8,
              gap: 5,
            }}
          >
            <TabNavigator />
          </ScrollView>

          {/* Message Type Selector - Right side with fixed width */}
          <View style={{
            width: 100,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            paddingRight: 8,
          }}>
            <View style={[
              styles.modernActionButton,
              getModernActionStyle(theme, 'blue'),
              {
                padding: 12,
                maxWidth: 50,
                minWidth: 50,
                borderWidth: 1,
                marginVertical: -5,
                borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(16px)',
                }),
              }
            ]}>
            <Popover
              open={!!messageState}
              anchorEl={messageState}
              onClose={() => setMessageState(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: '24px',
                  border: `1px solid ${theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)'}`,
                  backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
                  backdropFilter: 'blur(24px)',
                  boxShadow: theme
                    ? '0 12px 48px rgba(200, 162, 200, 0.2)'
                    : '0 12px 48px rgba(135, 206, 235, 0.2)',
                  minWidth: '360px',
                  maxWidth: '420px',
                  padding: 0,
                  overflow: 'hidden',
                  elevation: 12,
                }
              }}
            >
              {/* Header */}
              <View style={{
                  backgroundColor: theme ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                paddingHorizontal: 24,
                paddingVertical: 20,
                borderBottomWidth: 1,
                  borderBottomColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
                  ...(Platform.OS === 'web' && {
                    backdropFilter: 'blur(10px)',
                  }),
              }}>
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                }}>
                  <View style={{
                      backgroundColor: theme ? "rgba(200, 162, 200, 0.2)" : "rgba(135, 206, 235, 0.2)",
                    borderRadius: 12,
                    padding: 10,
                  }}>
                    <FontAwesome6
                      name="message"
                      size={20}
                        color={theme ? "#C8A2C8" : "#87CEEB"}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4,
                    }}>
                      Messaging Platforms
                    </Text>
                    <Text style={{
                      fontSize: 14,
                      color: theme ? '#64748b' : '#94a3b8',
                      fontWeight: '500',
                    }}>
                      Select a platform to switch
                    </Text>
                  </View>
                </View>
              </View>

              {/* Content */}
              <View style={{ padding: 24 }}>
                <View style={{ gap: 12 }}>
                  {availableFeatures.map((feature, index) => (
                    <MenuItem
                      key={`${feature.title}-${index}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 16,
                        paddingLeft: 20,
                        paddingRight: 20,
                        paddingTop: 16,
                        paddingBottom: 16,
                        marginBottom: 0,
                        borderRadius: 16,
                        backgroundColor: feature.title === messageAppFilter
                          ? (theme ? 'rgba(200, 162, 200, 0.2)' : 'rgba(135, 206, 235, 0.2)')
                          : (theme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'),
                        display: 'flex',
                        minHeight: 64,
                        transition: 'all 0.2s ease',
                        borderWidth: 1,
                        borderColor: feature.title === messageAppFilter
                          ? (theme ? 'rgba(200, 162, 200, 0.5)' : 'rgba(135, 206, 235, 0.5)')
                          : (theme ? 'rgba(200, 162, 200, 0.2)' : 'rgba(135, 206, 235, 0.2)'),
                        borderStyle: 'solid',
                      }}
                      onClick={() => handleFeatureSelect(feature)}
                      sx={{
                        minWidth: 0,
                        typography: 'body1',
                        py: 0,
                        px: 0,
                        '&:hover': {
                          backgroundColor: feature.title === messageAppFilter
                            ? (theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)')
                            : (theme ? 'rgba(200, 162, 200, 0.15)' : 'rgba(135, 206, 235, 0.15)'),
                          transform: 'translateY(-2px)',
                          borderColor: feature.title === messageAppFilter
                            ? (theme ? 'rgba(200, 162, 200, 0.6)' : 'rgba(135, 206, 235, 0.6)')
                            : (theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)'),
                          boxShadow: theme
                            ? '0 4px 12px rgba(200, 162, 200, 0.25)'
                            : '0 4px 12px rgba(135, 206, 235, 0.25)',
                        }
                      }}
                    >
                      <View style={[
                        {
                          width: 48,
                          height: 48,
                          borderRadius: 24,
                          marginRight: 16,
                          position: 'relative',
                          borderWidth: 1,
                          borderColor: feature.title === messageAppFilter
                            ? (theme ? 'rgba(200, 162, 200, 0.5)' : 'rgba(135, 206, 235, 0.5)')
                            : (theme ? 'rgba(200, 162, 200, 0.2)' : 'rgba(135, 206, 235, 0.2)'),
                          backgroundColor: feature.title === messageAppFilter
                            ? (theme ? 'rgba(200, 162, 200, 0.2)' : 'rgba(135, 206, 235, 0.2)')
                            : 'transparent',
                          alignItems: 'center',
                          justifyContent: 'center',
                          elevation: feature.title === messageAppFilter ? 4 : 2,
                          shadowColor: theme ? '#C8A2C8' : '#87CEEB',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: feature.title === messageAppFilter ? 0.2 : 0.1,
                          shadowRadius: 8,
                        }
                      ]}>
                        {feature.icon}
                        {feature.badgeCount > 0 && (
                          <View style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            backgroundColor: '#ef4444',
                            borderRadius: 14,
                            minWidth: 28,
                            height: 28,
                            borderWidth: 3,
                            borderColor: theme ? '#ffffff' : '#1e293b',
                            elevation: 6,
                            shadowColor: '#ef4444',
                            shadowOffset: { width: 0, height: 3 },
                            shadowOpacity: 0.4,
                            shadowRadius: 10,
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingHorizontal: 4,
                          }}>
                            <Text style={{
                              color: 'white',
                              fontSize: 11,
                              fontWeight: '800',
                              textAlign: 'center',
                            }}>
                              {feature.badgeCount > 99 ? '99+' : feature.badgeCount}
                            </Text>
                          </View>
                        )}
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{
                          color: getModernTextColor(theme, 'primary'),
                          fontSize: 16,
                          fontWeight: '600',
                          marginBottom: 2,
                        }}>
                          {feature.title === 'default' ? "SmartSaaS" : toTitleCase(feature.title)}
                        </Text>
                        <Text style={{
                          color: theme ? '#64748b' : '#94a3b8',
                          fontSize: 12,
                          fontWeight: '500',
                        }}>
                          {feature.title === 'default'
                            ? 'Default messaging'
                            : `Switch to ${toTitleCase(feature.title)}`}
                        </Text>
                      </View>
                      {feature.title === messageAppFilter && (
                        <View style={{
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          backgroundColor: theme ? '#C8A2C8' : '#87CEEB',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <FontAwesome6
                            name="check"
                            size={14}
                            color="white"
                          />
                        </View>
                      )}
                    </MenuItem>
                  ))}
                </View>
              </View>
            </Popover>
            <Pressable 
              onPress={(e: any) => setMessageState(e.currentTarget)}
              onLongPress={handleDirectTabClick}
              delayLongPress={500}
              style={({ pressed }) => [
                {
                  backgroundColor: pressed
                    ? (theme ? 'rgba(200, 162, 200, 0.4)' : 'rgba(135, 206, 235, 0.4)')
                    : (theme ? 'rgba(200, 162, 200, 0.2)' : 'rgba(135, 206, 235, 0.2)'),
                  padding: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 1,
                  borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
                  width: 56,
                  minHeight: 56,
                  elevation: pressed ? 8 : 4,
                  shadowColor: theme ? '#C8A2C8' : '#87CEEB',
                  shadowOffset: { width: 0, height: pressed ? 4 : 2 },
                  shadowOpacity: pressed ? 0.3 : 0.2,
                  shadowRadius: pressed ? 12 : 8,
                  transform: [{ scale: pressed ? 0.95 : 1 }],
                  ...(Platform.OS === 'web' && {
                    backdropFilter: 'blur(12px)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                  }),
                }
              ]}
            >
              <View style={{
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                width: 48,
                height: 48,
              }}>
                {currentFeature?.icon}
                {getTotalBadgeCount() > 0 && (
                  <View style={{
                    position: 'absolute',
                    top: -12,
                    right: -12,
                    backgroundColor: '#ef4444',
                    borderRadius: 16,
                    minWidth: 28,
                    height: 28,
                    borderWidth: 3,
                      borderColor: theme ? 'rgba(200, 162, 200, 0.95)' : 'rgba(135, 206, 235, 0.95)',
                    elevation: 6,
                    shadowColor: '#ef4444',
                    shadowOffset: { width: 0, height: 3 },
                    shadowOpacity: 0.4,
                    shadowRadius: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingHorizontal: 4,
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(8px)',
                    }),
                  }}>
                    <Text style={{
                      color: 'white',
                      fontSize: 11,
                      fontWeight: '800',
                      textAlign: 'center',
                    }}>
                      {getTotalBadgeCount() > 99 ? '99+' : getTotalBadgeCount()}
                    </Text>
                  </View>
                )}
              </View>
            </Pressable>
            </View>
          </View>
        </View>
      </LinearGradient>
    </View>
  );
}

export default memo(FooterBar);